import React, { createContext, useContext, useState, useEffect } from 'react';
import moment from 'moment';
import logger from '../../../utils/logger';
// Create the context
const DashboardSettingsContext = createContext();

/**
 * Default global settings for all charts
 */
const DEFAULT_GLOBAL_SETTINGS = {
  startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
  endDate: moment().format('YYYY-MM-DD'),
  chartType: 'line',
  timeView: 'weekly',
  selectedModule: 'all',
};

/**
 * Provider component for dashboard settings
 * Manages global settings and component-specific settings
 */
export const DashboardSettingsProvider = ({ children, initialSettings = {} }) => {
  // Global settings state
  const [globalSettings, setGlobalSettings] = useState({
    ...DEFAULT_GLOBAL_SETTINGS,
    ...initialSettings,
  });

  // Component-specific settings
  const [componentSettings, setComponentSettings] = useState({});

  // Components that are detached from global settings
  const [detachedComponents, setDetachedComponents] = useState(new Set());

  // Selected component for settings panel
  const [selectedComponent, setSelectedComponent] = useState(null);
  
  // Settings panel open state
  const [settingsSidePanelOpen, setSettingsSidePanelOpen] = useState(false);

  // Load settings from localStorage on mount
  useEffect(() => {
    const loadSettings = () => {
      try {
        // Load global settings
        const storedGlobalSettings = localStorage.getItem('gsd-global-chart-settings');
        if (storedGlobalSettings) {
          setGlobalSettings(JSON.parse(storedGlobalSettings));
        }
        
        // Load component settings
        const storedComponentSettings = localStorage.getItem('gsd-component-chart-settings');
        if (storedComponentSettings) {
          setComponentSettings(JSON.parse(storedComponentSettings));
        }
        
        // Load detached components
        const storedDetachedComponents = localStorage.getItem('gsd-detached-chart-components');
        if (storedDetachedComponents) {
          setDetachedComponents(new Set(JSON.parse(storedDetachedComponents)));
        }
      } catch (error) {
        logger.error('Error loading settings from localStorage:', error);
      }
    };
    
    loadSettings();
  }, []);

  useEffect(() => {
    const saveSettings = () => {
      try {
        localStorage.setItem('gsd-global-chart-settings', JSON.stringify(globalSettings));
        localStorage.setItem('gsd-component-chart-settings', JSON.stringify(componentSettings));
        localStorage.setItem('gsd-detached-chart-components', JSON.stringify([...detachedComponents]));
      } catch (error) {
        logger.error('Error saving settings to localStorage:', error);
      }
    };
    
    saveSettings();
  }, [globalSettings, componentSettings, detachedComponents]);

  /**
   * Update global settings
   * @param {Object} newSettings - New settings to apply globally
   */
  const updateGlobalSettings = (newSettings) => {
    setGlobalSettings(prev => ({
      ...prev,
      ...newSettings,
    }));
  };

  /**
   * Open the settings panel for a component
   * @param {string} componentId - ID of the component to edit
   */
  const openSettingsPanel = (componentId) => {
    setSelectedComponent(componentId);
    setSettingsSidePanelOpen(true);
  };

  /**
   * Close the settings panel
   */
  const closeSettingsPanel = () => {
    setSettingsSidePanelOpen(false);
    setSelectedComponent(null);
  };

  /**
   * Check if a component is detached from global settings
   * @param {string} componentId - ID of the component to check
   * @returns {boolean} True if the component is detached
   */
  const isDetached = (componentId) => {
    return detachedComponents.has(componentId);
  };

  /**
   * Get the effective settings for a component
   * @param {string} componentId - ID of the component
   * @returns {Object} Settings for the component (local or global)
   */
  const getComponentSettings = (componentId) => {
    if (isDetached(componentId) && componentSettings[componentId]) {
      return componentSettings[componentId];
    } else {
      return globalSettings;
    }
  };

  /**
   * Update settings for a specific component
   * @param {string} componentId - ID of the component to update
   * @param {Object} newSettings - New settings to apply
   * @param {boolean} detach - Whether to detach from global settings
   */
  const updateComponentSettings = (componentId, newSettings, detach = false) => {
    // Update component settings
    setComponentSettings(prev => ({
      ...prev,
      [componentId]: {
        ...(prev[componentId] || globalSettings),
        ...newSettings,
      }
    }));

    // Update detached state
    if (detach && !detachedComponents.has(componentId)) {
      const newDetached = new Set(detachedComponents);
      newDetached.add(componentId);
      setDetachedComponents(newDetached);
    } else if (!detach && detachedComponents.has(componentId)) {
      const newDetached = new Set(detachedComponents);
      newDetached.delete(componentId);
      setDetachedComponents(newDetached);
    }
  };

  /**
   * Reattach a component to global settings
   * @param {string} componentId - ID of the component to reattach
   */
  const reattachToGlobal = (componentId) => {
    // Remove from detached set
    if (detachedComponents.has(componentId)) {
      const newDetached = new Set(detachedComponents);
      newDetached.delete(componentId);
      setDetachedComponents(newDetached);
    }

    // Clean up component settings (optional)
    setComponentSettings(prev => {
      const newSettings = { ...prev };
      delete newSettings[componentId];
      return newSettings;
    });
  };

  // Context value
  const contextValue = {
    // Settings state
    globalSettings,
    componentSettings,
    detachedComponents,
    selectedComponent,
    settingsSidePanelOpen,
    
    // Actions
    updateGlobalSettings,
    openSettingsPanel,
    closeSettingsPanel,
    isDetached,
    getComponentSettings,
    updateComponentSettings,
    reattachToGlobal,
  };

  return (
    <DashboardSettingsContext.Provider value={contextValue}>
      {children}
    </DashboardSettingsContext.Provider>
  );
};

/**
 * Hook to use dashboard settings
 * @returns {Object} Dashboard settings context
 */
export const useDashboardSettings = () => {
  const context = useContext(DashboardSettingsContext);
  if (context === undefined) {
    throw new Error('useDashboardSettings must be used within a DashboardSettingsProvider');
  }
  return context;
};

export default DashboardSettingsContext;